import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Globe, Linkedin, BookOpen, Calendar, UserPlus, User as UserIcon } from 'lucide-react';
import { notify } from '../utils/notifications';
import { AvatarImage } from '../components/AvatarImage';
import { useEventData } from '../context/EventDataContext';
import { useFavorites } from '../hooks/useFavorites';
import { useApp } from '../context/AppContext';
import { useEventModules } from '../hooks/useEventModules';

interface SessionBrief {
  id: string;
  title: string;
  type?: string | null;
  date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  room?: string | null;
  status: string;
}

interface PaperBrief {
  id: string;
  title: string;
  authors: string[];
  processing_status: string;
}

interface SpeakerDetail {
  id: string;
  linked_user_id?: string | null;
  name: string;
  title?: string | null;
  company?: string | null;
  bio?: string | null;
  avatar_url?: string | null;
  linkedin?: string | null;
  portfolio?: string | null;
  research_interests?: string | null;
  sessions: SessionBrief[];
  papers: PaperBrief[];
}

function fmtTime(t?: string | null): string {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function fmtDate(d?: string | null): string {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function SpeakerProfileScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { currentEventId, authSession, isAuthSessionCurrent } = useApp();
  const { isEnabled, labelFor } = useEventModules();
  const { preview, services: { speakerService } } = useEventData();
  const [speaker, setSpeaker] = useState<SpeakerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState<'sessions' | 'papers'>('sessions');

  const saved = speaker ? isFavorite(speaker.id, 'speaker') : false;
  const agendaEnabled = isEnabled('agenda');
  const papersEnabled = isEnabled('papers');
  const visibleTab = activeTab === 'sessions' && agendaEnabled
    ? 'sessions'
    : activeTab === 'papers' && papersEnabled
      ? 'papers'
      : agendaEnabled
        ? 'sessions'
        : papersEnabled
          ? 'papers'
          : null;

  useEffect(() => {
    let cancelled = false;
    const owner = authSession;
    const isCurrent = () => preview || (!!owner && isAuthSessionCurrent(owner));
    setSpeaker(null);
    setNotFound(false);
    if (
      !id
      || !currentEventId
      || !isCurrent()
    ) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    speakerService.getById(
      id,
      currentEventId,
      preview ? undefined : { signal: owner!.signal },
    )
      .then((data) => {
        if (cancelled || !isCurrent()) return;
        if (!data) { setNotFound(true); return; }
        setSpeaker(data as unknown as SpeakerDetail);
      })
      .catch(() => {
        if (!cancelled && isCurrent()) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled && isCurrent()) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [authSession, currentEventId, id, isAuthSessionCurrent, preview, speakerService]);

  if (loading) {
    return (
      <div className="size-full flex items-center justify-center" style={{ background: 'var(--app-bg)' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-[var(--app-accent-solid)] border-t-transparent animate-spin" />
          <p className="text-[var(--app-text-muted)] text-sm">Loading speaker…</p>
        </div>
      </div>
    );
  }

  if (notFound || !speaker) {
    return (
      <div className="size-full flex flex-col items-center justify-center gap-4" style={{ background: 'var(--app-bg)' }}>
        <UserIcon className="w-12 h-12 text-[var(--app-text-subtle)]" />
        <p className="text-[var(--app-text-muted)]">Speaker not found</p>
        <button onClick={() => navigate(-1)} className="text-[var(--app-accent-solid)] text-sm">Go back</button>
      </div>
    );
  }

  return (
    <div className="size-full flex flex-col overflow-hidden" style={{ background: 'var(--app-bg)' }}>
      {/* Sticky top bar */}
      <div
        className="shrink-0 flex items-center px-4 py-3 border-b"
        style={{ background: 'var(--app-nav-bg)', borderColor: 'var(--app-divider)' }}
      >
        <button
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="size-9 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style={{ background: 'var(--app-surface)' }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
        </button>
        <span className="ml-3 text-sm font-medium truncate" style={{ color: 'var(--app-text)' }}>Speaker Profile</span>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 pb-8">
          {/* Hero: avatar + name */}
          <div className="flex flex-col items-center pt-6 pb-5">
            <div
              className="size-24 rounded-full overflow-hidden flex items-center justify-center mb-4 border-2"
              style={{ borderColor: 'var(--app-accent-border)', background: 'var(--app-surface)' }}
            >
              <AvatarImage source={speaker.avatar_url} alt={speaker.name} className="w-full h-full object-cover object-top" fallback={<UserIcon className="w-10 h-10" style={{ color: 'var(--app-text-subtle)' }} />} />
            </div>
            <h1 className="text-[var(--app-text)] text-2xl text-center">{speaker.name}</h1>
            {speaker.title && <p className="text-[var(--app-text-muted)] text-sm mt-1 text-center">{speaker.title}</p>}
            {speaker.company && <p className="text-sm mt-0.5 text-center" style={{ color: 'var(--app-accent-solid)' }}>{speaker.company}</p>}
          </div>

          {/* Action Buttons */}
          {!preview && <div className="flex gap-3 mb-5">
            <button
              onClick={() => {
                if (speaker) toggleFavorite(speaker.id, 'speaker');
                notify.info(saved ? 'Removed from saved' : 'Speaker saved');
              }}
              className="flex-1 h-11 rounded-xl flex items-center justify-center gap-2 text-sm transition-colors active:scale-[0.98]"
              style={saved
                ? { background: 'var(--app-accent-solid)', color: '#fff' }
                : { background: 'var(--app-surface)', color: 'var(--app-text)', border: '1px solid var(--app-border)' }
              }
            >
              <UserPlus className="w-4 h-4" />
              {saved ? 'Saved' : 'Save'}
            </button>
          </div>}

          {/* Social Links */}
          {(speaker.linkedin || speaker.portfolio) && (
            <div className="flex gap-3 mb-5">
              {speaker.linkedin && (
                <button
                  onClick={() => window.open(speaker.linkedin!, '_blank')}
                  className="flex-1 h-10 rounded-xl flex items-center justify-center gap-1.5 text-xs active:scale-[0.98] transition-transform"
                  style={{ background: 'var(--app-surface)', color: 'var(--app-text-subtle)', border: '1px solid var(--app-border)' }}
                >
                  <Linkedin className="w-3.5 h-3.5" />
                  LinkedIn
                </button>
              )}
              {speaker.portfolio && (
                <button
                  onClick={() => window.open(speaker.portfolio!, '_blank')}
                  className="flex-1 h-10 rounded-xl flex items-center justify-center gap-1.5 text-xs active:scale-[0.98] transition-transform"
                  style={{ background: 'var(--app-surface)', color: 'var(--app-text-subtle)', border: '1px solid var(--app-border)' }}
                >
                  <Globe className="w-3.5 h-3.5" />
                  Website
                </button>
              )}
            </div>
          )}

          {/* Bio */}
          {speaker.bio && (
            <div className="mb-5">
              <h2 className="text-[var(--app-text)] text-base mb-2">About</h2>
              <p className="text-[var(--app-text-muted)] text-sm leading-relaxed">{speaker.bio}</p>
            </div>
          )}

          {/* Research Interests */}
          {speaker.research_interests && (
            <div className="mb-5">
              <h2 className="text-[var(--app-text)] text-base mb-2">Research Interests</h2>
              <p className="text-[var(--app-text-muted)] text-sm leading-relaxed">{speaker.research_interests}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            {agendaEnabled && (
            <button
              onClick={() => setActiveTab('sessions')}
              className="flex-1 py-2.5 rounded-xl text-sm flex items-center justify-center gap-1.5 transition-colors"
              style={visibleTab === 'sessions'
                ? { background: 'var(--app-accent-solid)', color: '#fff' }
                : { background: 'var(--app-surface)', color: 'var(--app-text-subtle)' }
              }
            >
              <Calendar className="w-4 h-4" />
              {labelFor('agenda')} ({speaker.sessions.length})
            </button>
            )}
            {papersEnabled && (
            <button
              onClick={() => setActiveTab('papers')}
              className="flex-1 py-2.5 rounded-xl text-sm flex items-center justify-center gap-1.5 transition-colors"
              style={visibleTab === 'papers'
                ? { background: 'var(--app-accent-solid)', color: '#fff' }
                : { background: 'var(--app-surface)', color: 'var(--app-text-subtle)' }
              }
            >
              <BookOpen className="w-4 h-4" />
              {labelFor('papers')} ({speaker.papers.length})
            </button>
            )}
          </div>

          {/* Tab Content */}
          <div className="flex flex-col gap-3">
            {visibleTab === 'sessions' && (
              speaker.sessions.length === 0 ? (
                <p className="text-[var(--app-text-subtle)] text-sm text-center py-6">No sessions scheduled</p>
              ) : (
                speaker.sessions.map(session => (
                  <button
                    key={session.id}
                    onClick={() => navigate(`/session/${session.id}`)}
                    className="rounded-xl p-4 border text-left active:scale-[0.98] transition-transform w-full"
                    style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
                  >
                    <p className="text-[var(--app-text)] text-sm">{session.title}</p>
                    <div className="flex items-center gap-2 mt-1.5 text-[var(--app-text-muted)] text-xs">
                      <Calendar className="w-3.5 h-3.5 shrink-0" />
                      <span>
                        {[fmtDate(session.date), session.start_time ? fmtTime(session.start_time) : null, session.room].filter(Boolean).join(' · ')}
                      </span>
                    </div>
                  </button>
                ))
              )
            )}
            {visibleTab === 'papers' && (
              speaker.papers.length === 0 ? (
                <p className="text-[var(--app-text-subtle)] text-sm text-center py-6">No papers linked</p>
              ) : (
                speaker.papers.map(paper => (
                  <button
                    key={paper.id}
                    onClick={() => navigate(`/paper/${paper.id}`)}
                    className="rounded-xl p-4 border text-left active:scale-[0.98] transition-transform w-full"
                    style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
                  >
                    <p className="text-[var(--app-text)] text-sm">{paper.title}</p>
                    {paper.authors?.length > 0 && (
                      <p className="text-[var(--app-text-muted)] text-xs mt-1">{paper.authors.slice(0, 3).join(', ')}</p>
                    )}
                  </button>
                ))
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
